import {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  PropsWithChildren,
} from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'
import { makePublicUrl } from 'app/utils/publicUrl'
import { commonMessages } from 'app/i18n/messages'
import ROUTES from 'app/routes'
import QrCodeReader from 'components/QrCodeReader'
import { QR_CODE_READER_ERRORS } from 'components/QrCodeReader/consts'
import NetworkWizardLayout from '../NetworkWizardLayout'
import { NetworkWizardContext } from '../NetworkWizardContext'
import { SpinnerCentered } from 'components/Loader'
import { CLAIM_DEVICE_ERRORS } from './consts'
import ClaimDeviceManuallyForm from './ClaimDeviceManuallyForm'
import AlreadyClaimedError from './ClaimDeviceErrorMessage/AlreadyClaimedError'
import QrCodeScanUnknownError from './ClaimDeviceErrorMessage/QrCodeScanUnknownError'
import QrCodeScanNotAllowedError from './ClaimDeviceErrorMessage/QrCodeScanNotAllowedError'
import BadDeviceDataError from './ClaimDeviceErrorMessage/BadDeviceDataError'
import { parseDeviceDataFromQrCodeScan } from './utils'
import { claimDeviceThunk } from 'features/networkAi/slice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { mistOrganizationIdSelector } from '../../selectors'
import {
  useCurrentHomeSiteId,
  useNetworkHomeJobsRouteParams,
} from 'features/networkAi/hooks'
import ClaimDeviceNameForm from './ClaimDeviceNameForm'
import {
  NetworkWizardBottomPanel,
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../styles'
import {
  ClaimDeviceContainer,
  ClaimDeviceH4,
  ClaimDeviceSecondaryButton,
  ClaimDeviceWhiteBlock,
  ClaimDeviceHelperButton,
  DisplayCodeHelper,
} from './styles'
import { networkClaimMessages } from './messages'
import SuccessModal from 'components/Modal/SuccessModal'
import {
  subscribedAccessPointsSelector,
  claimDevicesFetchDataSelector,
} from 'features/networkAi/selectors'

// TODO: Localize
function ClaimDevice() {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()
  const organizationId = useAppSelector(mistOrganizationIdSelector)
  const siteId = useCurrentHomeSiteId({ homeId })
  const { handleCloseWizard } = useContext(NetworkWizardContext)
  const [manualMode, setManualMode] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<
    QR_CODE_READER_ERRORS | CLAIM_DEVICE_ERRORS | undefined
  >()
  const claimDevices = useAppSelector(claimDevicesFetchDataSelector.data)
  const subscribedAccessPoints = useAppSelector(subscribedAccessPointsSelector)
  const hasRemainingSubs =
    claimDevices !== null && subscribedAccessPoints.count > claimDevices?.length

  const [deviceAppName, setDeviceAppName] = useState('')
  const [deviceDataMac, setDeviceDataMac] = useState('')
  const [deviceDataCode, setDeviceDataCode] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const submitIsDisabled =
    !deviceAppName ||
    !deviceDataMac ||
    !deviceDataCode ||
    !organizationId ||
    isLoading

  const resetDeviceData = () => {
    setError(undefined)
    setDeviceDataMac('')
    setDeviceDataCode('')
    setDeviceAppName('')
  }

  const onQrCodeReaderResult = (text: string) => {
    setIsLoading(true)
    const draftData = parseDeviceDataFromQrCodeScan(text)
    // TODO: check device already claimed and if yes then set error ALREADY_CLAIMED
    setDeviceDataMac(`${draftData.mac}`)
    setDeviceDataCode(`${draftData.code}`)
    setIsLoading(false)
  }

  const onQrCodeReaderError = (error: QR_CODE_READER_ERRORS) => {
    if (!manualMode) {
      setError(error)
    }
  }

  const submitClaimDevice = useCallback(async () => {
    try {
      setIsLoading(true)
      await dispatch(
        claimDeviceThunk(organizationId, siteId, {
          mac: deviceDataMac,
          code: deviceDataCode,
          name: deviceAppName,
        })
      )

      const path = generatePath(ROUTES.networkAi.wizard.devicesList, {
        rootDomainId,
        jobId,
        homeId,
      })
      setIsLoading(false)

      return history.push(path)
    } catch (err) {
      setIsLoading(false)

      if (err?.toString()==='Error: Request failed with status code 400'){
        resetDeviceData()

        return setError(CLAIM_DEVICE_ERRORS.ALREADY_CLAIMED)
      }
    
      const errorDevicesArray = err?.response?.data?.error

      if (err?.response.data.reason[0] === 'belongs to another org') {
        resetDeviceData()

        return setError(CLAIM_DEVICE_ERRORS.ALREADY_CLAIMED)
      }

      if (
        Array.isArray(errorDevicesArray) &&
        errorDevicesArray.includes(deviceDataMac.toLowerCase())
      ) {
        resetDeviceData()

        return setError(CLAIM_DEVICE_ERRORS.ALREADY_CLAIMED)
      }

      if (err?.response?.data?.msg === CLAIM_DEVICE_ERRORS.BAD_DEVICE_DATA) {
        resetDeviceData()

        return setError(CLAIM_DEVICE_ERRORS.BAD_DEVICE_DATA)
      }

      return setError(CLAIM_DEVICE_ERRORS.ALREADY_CLAIMED)
    }
  }, [
    rootDomainId,
    jobId,
    siteId,
    homeId,
    organizationId,
    deviceDataCode,
    deviceDataMac,
    deviceAppName,
    dispatch,
    history,
  ])

  interface DeleayComponentProps {
    time: number
  }

  function DeleayComponent({
    time,
    children,
  }: PropsWithChildren<DeleayComponentProps>) {
    const [show, setShow] = useState(false)

    useEffect(() => {
      const timeout = setTimeout(() => {
        setShow(true)
      }, time)

      return () => clearTimeout(timeout)
    }, [show, time])

    if (!show) return null

    return <>{children}</>
  }

  const bottomBlock = useMemo(() => {
    return (
      <NetworkWizardBottomPanel>
        <NetworkWizardCancelButton
          variant="secondary"
          type="button"
          onClick={handleCloseWizard}
        >
          {formatMessage(commonMessages.skip)}
        </NetworkWizardCancelButton>

        <NetworkWizardSubmitButton
          variant="primary"
          type="submit"
          disabled={submitIsDisabled}
          onClick={submitClaimDevice}
        >
          {formatMessage(commonMessages.next)}
        </NetworkWizardSubmitButton>
      </NetworkWizardBottomPanel>
    )
  }, [submitClaimDevice, formatMessage, submitIsDisabled, handleCloseWizard])

  return (
    <>
      <SuccessModal
        title={formatMessage(networkClaimMessages.popupTitle)}
        isOpen={isModalOpen}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={() => setIsModalOpen(false)}
      >
        <DisplayCodeHelper
          src={makePublicUrl(`images/networkIcons/AP33.jpg`)}
          alt="AP33"
        />
      </SuccessModal>

      <NetworkWizardLayout bottomBlock={bottomBlock} currentWizardStepId={3}>
        <ClaimDeviceContainer>
          <NetworkWizardTitle>
            {formatMessage(networkClaimMessages.claimDevices)}
          </NetworkWizardTitle>

          {!hasRemainingSubs ? (
            <ClaimDeviceWhiteBlock>
              <ClaimDeviceH4>No Subscriptions Remaining</ClaimDeviceH4>
              <>
                Please purchase more subscriptions to claim additional devices.
              </>
            </ClaimDeviceWhiteBlock>
          ) : (
            <>
              {deviceDataMac && deviceDataCode ? (
                <ClaimDeviceNameForm
                  onEraseDataClick={resetDeviceData}
                  deviceDataMac={deviceDataMac}
                  deviceDataCode={deviceDataCode}
                  deviceAppName={deviceAppName}
                  setDeviceAppName={setDeviceAppName}
                />
              ) : (
                <ClaimDeviceWhiteBlock>
                  <ClaimDeviceH4>
                    {manualMode
                      ? formatMessage(networkClaimMessages.claimManually)
                      : formatMessage(networkClaimMessages.scanQR)}
                  </ClaimDeviceH4>

                  {isLoading && <SpinnerCentered />}

                  {error === CLAIM_DEVICE_ERRORS.ALREADY_CLAIMED && (
                    <AlreadyClaimedError onEraseDataClick={resetDeviceData} />
                  )}
                  {error === CLAIM_DEVICE_ERRORS.BAD_DEVICE_DATA && (
                    <BadDeviceDataError onEraseDataClick={resetDeviceData} />
                  )}
                  {error === QR_CODE_READER_ERRORS.UNKNOWN_ERROR && (
                    <>
                      <QrCodeScanUnknownError
                        onEraseDataClick={resetDeviceData}
                      />
                      <ClaimDeviceSecondaryButton
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          resetDeviceData()
                          setManualMode(true)
                        }}
                        disabled={isLoading}
                      >
                        {formatMessage(networkClaimMessages.claimManually)}
                      </ClaimDeviceSecondaryButton>
                    </>
                  )}
                  {error === QR_CODE_READER_ERRORS.NOT_ALLOWED_ERROR && (
                    <>
                      <QrCodeScanNotAllowedError
                        onEraseDataClick={resetDeviceData}
                      />
                      <ClaimDeviceSecondaryButton
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          resetDeviceData()
                          setManualMode(true)
                        }}
                        disabled={isLoading}
                      >
                        {formatMessage(networkClaimMessages.claimManually)}
                      </ClaimDeviceSecondaryButton>
                    </>
                  )}

                  {!error && !isLoading && !manualMode && (
                    <>
                      <QrCodeReader
                        height={'328px'}
                        width={'308px'}
                        onResult={onQrCodeReaderResult}
                        onError={onQrCodeReaderError}
                      />
                      <DeleayComponent time={5000}>
                        <ClaimDeviceSecondaryButton
                          variant="secondary"
                          type="button"
                          onClick={() => setManualMode(true)}
                          disabled={isLoading}
                        >
                          {formatMessage(networkClaimMessages.claimManually)}
                        </ClaimDeviceSecondaryButton>
                      </DeleayComponent>
                    </>
                  )}

                  {!error && !isLoading && manualMode && (
                    <>
                      <ClaimDeviceManuallyForm
                        setDeviceDataCode={setDeviceDataCode}
                        setDeviceDataMac={setDeviceDataMac}
                      />

                      <ClaimDeviceSecondaryButton
                        variant="secondary"
                        type="button"
                        onClick={() => setManualMode(false)}
                        disabled={isLoading}
                      >
                        {formatMessage(networkClaimMessages.scanQRTitle)}
                      </ClaimDeviceSecondaryButton>
                    </>
                  )}
                  <ClaimDeviceHelperButton onClick={() => setIsModalOpen(true)}>
                    {formatMessage(networkClaimMessages.whereCodeButtonText)}
                  </ClaimDeviceHelperButton>
                </ClaimDeviceWhiteBlock>
              )}
            </>
          )}
        </ClaimDeviceContainer>
      </NetworkWizardLayout>
    </>
  )
}

export default ClaimDevice
